import React from 'react';
import { Button, RoundButton } from '../button';
import { Div, Flex } from '../box';
import Icon from '../icon';
import { Input, InputContainer } from '../input';
import Text from '../text';

type SelectCreateRowProps<Value extends string> = {
  onCreate: (name: string) => Value | Promise<Value>;
  onCreated: (newValue: Value) => void;
  createLabel: string;
  createPlaceholder?: string;
  createButtonLabel: string;
  createNewValidate?: (draft: string) => string | null;
};

type State = { status: 'idle' } | { status: 'editing'; draft: string } | { status: 'submitting'; draft: string };

type Action =
  | { type: 'start' }
  | { type: 'change'; draft: string }
  | { type: 'cancel' }
  | { type: 'submit' }
  | { type: 'submit-succeeded' }
  | { type: 'submit-failed' };

const INITIAL_STATE: State = { status: 'idle' };

function reduce(state: State, action: Action): State {
  switch (action.type) {
    case 'start':
      return { status: 'editing', draft: '' };
    case 'change':
      return state.status === 'editing' ? { status: 'editing', draft: action.draft } : state;
    case 'cancel':
      return state.status === 'submitting' ? state : INITIAL_STATE;
    case 'submit':
      return state.status === 'editing' ? { status: 'submitting', draft: state.draft } : state;
    case 'submit-succeeded':
      return INITIAL_STATE;
    case 'submit-failed':
      return state.status === 'submitting' ? { status: 'editing', draft: state.draft } : state;
  }
}

export function SelectCreateRow<Value extends string>({
  onCreate,
  onCreated,
  createLabel,
  createPlaceholder,
  createButtonLabel,
  createNewValidate,
}: SelectCreateRowProps<Value>) {
  const [state, dispatch] = React.useReducer(reduce, INITIAL_STATE);
  const draft = state.status === 'idle' ? '' : state.draft;
  const isSubmitting = state.status === 'submitting';
  const error = state.status === 'editing' && draft.length > 0 ? (createNewValidate?.(draft) ?? null) : null;
  const canSubmit = state.status === 'editing' && draft.trim().length > 0 && !error;

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }
    dispatch({ type: 'submit' });
    try {
      const newValue = await onCreate(draft.trim());
      onCreated(newValue);
      dispatch({ type: 'submit-succeeded' });
    } catch (error) {
      dispatch({ type: 'submit-failed' });
      throw error;
    }
  };

  if (state.status === 'idle') {
    return (
      <Flex padding={'xs'}>
        <Button
          block
          size={'sm'}
          variant={'secondary-action'}
          onClick={() => dispatch({ type: 'start' })}
          gap={'xs'}
          justifyContent={'flex-start'}
          weight={400}
        >
          <Icon name={'PLUS'} size={'xl'} weight={300} />
          {createLabel}
        </Button>
      </Flex>
    );
  }

  return (
    <Flex gap={'xs'} padding={'sm'} alignItems={'flex-start'}>
      <Div flex={1}>
        <InputContainer size={'md'} paddingRight={'xxl'}>
          <Input
            focusOnMount
            size={'md'}
            value={draft}
            disabled={isSubmitting}
            error={!!error}
            onChange={event => dispatch({ type: 'change', draft: event.target.value })}
            onKeyDown={async event => {
              // Stop Base UI Select's popup-level keydown handlers from swallowing keys.
              event.stopPropagation();
              if (event.key === 'Enter' && canSubmit) {
                event.preventDefault();
                await handleSubmit();
              }
              if (event.key === 'Escape') {
                event.preventDefault();
                dispatch({ type: 'cancel' });
              }
            }}
            placeholder={createPlaceholder}
          />
          {!isSubmitting && (
            <InputContainer.RightButtonContainer>
              <RoundButton
                onClick={() => dispatch({ type: 'cancel' })}
                variant={'secondary-clean'}
                aria-label={'Cancel'}
                size={'xs'}
              >
                <Icon name={'CLOSE'} variant={'inherit'} size={'sm'} />
              </RoundButton>
            </InputContainer.RightButtonContainer>
          )}
        </InputContainer>
        {error && (
          <Text size={'xs'} variant={'negative'} marginTop={'xxs'} aria-live={'polite'}>
            {error}
          </Text>
        )}
      </Div>
      <Button size={'sm'} variant={'action'} onClick={handleSubmit} disabled={!canSubmit} loading={isSubmitting}>
        {createButtonLabel}
      </Button>
    </Flex>
  );
}
